import { Box, Button, Popper, Typography } from "@mui/material";
import React, { useState, useEffect } from "react";
import FilterListIcon from "@mui/icons-material/FilterList";
import Grid from "@mui/material/Grid2";
import {
  CustomFormControl,
  MobileNumberInput,
  CustomFormControlNum,
  AutocompleteInput,
} from "_components";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { alertActions } from "_store";
import { ERR_MSG } from "constants/labels";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationSchema } from "./advanceFilterValidationSchema";

const AdvanceFilter = ({
  setFilterData,
  apiCall,
  setOpenRowId,
  setAccountNumber,
  resetSearch,
  defValueSearch,
  setSearchMessage,
}) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const id = isOpen && Boolean(anchorEl) ? "advance-filter-popper" : undefined;
  const header = "FILTER";

  const defaultValues = {
    Status: "",
    AccountNumber: null,
    FirstName: "",
    LastName: "",
    PhoneNumber: "",
    MobileNumber: "",
    HouseNumber: "",
    ApartmentOrSuiteNumber: "",
    StreetAddress: "",
    City: "",
    State: "",
  };

  const statusOptions = [
    { label: "Account Holder Name", value: "Account Holder Name" },
    { label: "Washington Gas Account", value: "Washington Gas Account" },
    { label: "Contact Number", value: "Contact" },
    { label: "Service Address", value: "Service Address" },
  ];

  const {
    control,
    handleSubmit,
    reset,
    watch,
    register,
    formState: { errors },
    trigger,
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
  });

  const formValues = watch();
  const selectedStatus = formValues.Status;

  const {
    Status,
    AccountNumber,
    PhoneNumber,
    MobileNumber,
    HouseNumber,
    StreetAddress,
    ...otherValues
  } = formValues;

  const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;

  const isFormValid = (() => {
    if (Status === "Washington Gas Account") {
      if (AccountNumber && AccountNumber.length < 12) {
        return false;
      }

      return AccountNumber && AccountNumber.length === 12;
    }

    if (Status === "Contact") {
      const isPhoneValid = PhoneNumber && phoneRegex.test(PhoneNumber);
      const isMobileValid = MobileNumber && phoneRegex.test(MobileNumber);
      const isHouseValid = HouseNumber && phoneRegex.test(HouseNumber);

      if (!PhoneNumber && !MobileNumber && !HouseNumber) return false;
      if (
        (PhoneNumber && !isPhoneValid) ||
        (MobileNumber && !isMobileValid) ||
        (HouseNumber && !isHouseValid)
      )
        return false;

      return true;
    }

    if (Status === "Service Address") {
      return StreetAddress && StreetAddress.trim() !== "";
    }

    return Object.values(otherValues).some(
      (value) => value !== null && value !== ""
    );
  })();

  useEffect(() => {
    if (selectedStatus) {
      reset({
        ...defaultValues,
        Status: selectedStatus,
      });
    }
  }, [selectedStatus, reset]);

  const onSubmit = async (data) => {
    dispatch(alertActions.clear());
    try {
      const formattedData = {
        AccountNumber: data.AccountNumber || null,
        FirstName: data.FirstName ? `${data.FirstName}*` : null,
        LastName: data.LastName ? `${data.LastName}*` : null,
        ContactNumber: data.MobileNumber || null,
        HouseNumber: data.HouseNumber || null,
        ApartmentOrSuiteNumber: data.ApartmentOrSuiteNumber || null,
        StreetAddress: data.StreetAddress ? `${data.StreetAddress}*` : null,
        City: data.City ? `${data.City}*` : null,
        State: data.State ? `${data.State}*` : null,
      };

      const res = await dispatch(apiCall(formattedData)).unwrap();

      if (res?.Succeeded === false) {
        dispatch(
          alertActions.error({
            message: res?.Message,
            header: "Energy Assistance",
          })
        );
        handleReset();
        setIsOpen(false);
        return;
      }

      const results = res?.Data?.Results || [];
      setFilterData(results);
      setSearchMessage(res?.Data?.Message);
      if (results.length === 1) {
        setOpenRowId(results[0]?.AccountNumber);
        setAccountNumber(results[0]?.AccountNumber);
      } else {
        setOpenRowId(null);
        setAccountNumber(null);
      }
      if (res?.Message && res?.Message !== "API call completed.") {
        dispatch(
          alertActions.error({
            message: res?.Message,
            header: "Energy Assistance",
          })
        );
      }
      setIsOpen(false);
      handleReset();
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: "Filter Failed",
        })
      );
      handleReset();
      setIsOpen(false);
    }
  };

  const handleReset = () => {
    reset(defaultValues);
  };

  const handleOpenFilter = (e) => {
    handleReset();
    setAnchorEl(e.currentTarget);
    setIsOpen(!isOpen);
    resetSearch(defValueSearch);
  };

  const handleBlur = async (e) => {
    const fieldName = e.target.name;
    await trigger(fieldName);
  };

  return (
    <>
      <Button
        className="Filter"
        type="button"
        variant="contained"
        color="primary"
        aria-describedby={id}
        onClick={handleOpenFilter}
      >
        <FilterListIcon />
        Filter
      </Button>

      <Popper
        id={id}
        open={isOpen}
        anchorEl={anchorEl}
        className="Filtercontainer marketerGroup Border"
      >
        <Box
          sx={{ border: 1, p: 1, bgcolor: "background.paper" }}
          className="Filtercontainerinner Border"
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="Registrationcontainer marketerFiltercontainer"
          >
            <Typography component="div" className="userprofilelist">
              <Grid container direction="row" spacing={{ xs: 2, md: 3 }}>
                <Grid item xs={12} sm={6} md={6}>
                  <Typography variant="h2" className="userprofilelistcontent">
                    {header}
                  </Typography>
                </Grid>
              </Grid>
            </Typography>

            <AutocompleteInput
              control={control}
              name="Status"
              label="Filter By"
              options={statusOptions}
            />

            {selectedStatus === "Account Holder Name" && (
              <>
                <CustomFormControl
                  required={false}
                  id="FirstName"
                  label="First Name"
                  type="text"
                  register={register}
                  errors={errors}
                />
                <CustomFormControl
                  required={false}
                  id="LastName"
                  label="Last Name"
                  type="text"
                  register={register}
                  errors={errors}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    paddingRight: "16px",
                    paddingLeft: "16px",
                  }}
                >
                  Searching by{" "}
                  <Typography
                    component={"span"}
                    sx={{ fontStyle: "italic", fontSize: "12px" }}
                  >
                    first name
                  </Typography>{" "}
                  only may result in longer processing times. For faster and
                  more accurate results, please enter{" "}
                  <Typography
                    component={"span"}
                    sx={{ fontWeight: 700, fontSize: "12px" }}
                  >
                    all available details
                  </Typography>{" "}
                  {
                    "(such as last name or use additional search functionality including address, account number, or other identifiers)."
                  }
                </Typography>
              </>
            )}

            {selectedStatus === "Washington Gas Account" && (
              <>
                <CustomFormControl
                  id="AccountNumber"
                  label="Account Number"
                  type="text"
                  register={register}
                  errors={errors}
                  handleBlur={handleBlur}
                />
              </>
            )}

            {selectedStatus === "Contact" && (
              <>
                <MobileNumberInput
                  control={control}
                  name="MobileNumber"
                  label="Phone/Mobile Number"
                  errors={errors}
                  handleBlur={handleBlur}
                />
              </>
            )}

            {selectedStatus === "Service Address" && (
              <>
                <CustomFormControl
                  id="HouseNumber"
                  label="House Number"
                  type="number"
                  register={register}
                  errors={errors}
                  handleBlur={handleBlur}
                />
                <CustomFormControl
                  required={false}
                  id="ApartmentOrSuiteNumber"
                  label="Apartment/Suite #"
                  type="text"
                  register={register}
                  errors={errors}
                />
                <CustomFormControl
                  required={false}
                  id="StreetAddress"
                  label="Street Address (Required)"
                  type="text"
                  register={register}
                  errors={errors}
                />
                <CustomFormControl
                  required={false}
                  id="City"
                  label="City"
                  type="text"
                  register={register}
                  errors={errors}
                />
                <CustomFormControl
                  required={false}
                  id="State"
                  label="State"
                  type="text"
                  register={register}
                  errors={errors}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    paddingRight: "16px",
                    paddingLeft: "16px",
                  }}
                >
                  Searching by{" "}
                  <Typography
                    component={"span"}
                    sx={{ fontStyle: "italic", fontSize: "12px" }}
                  >
                    partial address information
                  </Typography>{" "}
                  may result in longer processing times. For faster and more
                  accurate results, please enter{" "}
                  <Typography
                    component={"span"}
                    sx={{ fontWeight: 700, fontSize: "12px" }}
                  >
                    all available details
                  </Typography>{" "}
                  {
                    "(such as full address or use additional search functionality including name, account number, or other identifiers)."
                  }
                </Typography>
              </>
            )}

            <Typography component="div" className="CreateMarketerbutton">
              <Button
                type="submit"
                variant="contained"
                className="submitbutton"
                color="primary"
                disabled={!isFormValid}
              >
                Search
              </Button>
              <Button
                variant="contained"
                color="red"
                className="cancelbutton"
                onClick={() => {
                  setIsOpen(false);
                  handleReset();
                }}
              >
                Cancel
              </Button>
            </Typography>
          </form>
        </Box>
      </Popper>
    </>
  );
};

export default AdvanceFilter;
